import { fg } from '@confluence/feature-gating';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { PAGE_TREE_STATUSES } from '@confluence/page-tree/entry-points/pageTreeStatuses.ts';
import { preloadPageTree } from '@confluence/page-tree/entry-points/preloadPageTree.ts';

import {
	WhiteboardUnifiedQuery,
	WhiteboardUnifiedQuery_Relay,
} from './graphql/WhiteboardUnifiedQuery.graphql.ts';
import type {
	SpaceQuery as SpaceQueryType,
	SpaceQueryVariables,
} from './graphql/__types__/SpaceQuery.ts';
import { SpaceQuery, SpaceQuery_Relay } from './graphql/SpaceQuery.graphql.ts';
import type {
	WhiteboardUnifiedQueryVariables,
	WhiteboardUnifiedQuery as WhiteboardUnifiedQueryType,
} from './graphql/__types__/WhiteboardUnifiedQuery.ts';
import {
	ContentIdByReferenceIdQuery,
	ContentIdByReferenceIdQuery_Relay,
} from './graphql/ContentIdByReferenceIdQuery.graphql.ts';
import type {
	ContentIdByReferenceIdQuery as ContentIdByReferenceIdQueryType,
	ContentIdByReferenceIdQueryVariables,
} from './graphql/__types__/ContentIdByReferenceIdQuery.ts';
import type {
	ContentIdByReferenceIdQuery$data,
	ContentIdByReferenceIdQuery$variables,
} from './graphql/__generated__/ContentIdByReferenceIdQuery.graphql.ts';
import type {
	WhiteboardUnifiedQuery$data,
	WhiteboardUnifiedQuery$variables,
} from './graphql/__generated__/WhiteboardUnifiedQuery.graphql.ts';
import type {
	SpaceQuery$data,
	SpaceQuery$variables,
} from './graphql/__generated__/SpaceQuery.graphql.ts';

type PreloadWhiteboardDataParams = {
	spaceKey: string;
	contentId: string;
	skipPageTree?: boolean;
};

export async function preloadWhiteboardData({
	spaceKey,
	contentId,
	skipPageTree = false,
}: PreloadWhiteboardDataParams): Promise<any> {
	const preloadPromises = [preloadContentData(contentId, spaceKey), preloadSpace(spaceKey)];
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && !skipPageTree) {
		preloadPromises.push(
			preloadPageTree({
				spaceKey,
				statuses: PAGE_TREE_STATUSES,
				isSuperAdmin: false,
				contentId,
			}),
		);
	}
	return waitForAllReturnWhenError(preloadPromises);
}

async function preloadContentData(contentId: string, spaceKey: string): Promise<void> {
	const UUIDRegex = /[-]/;
	const isUuidBasedId = UUIDRegex.test(contentId);
	if (isUuidBasedId) {
		if (fg('confluence_whiteboard-common-apollo-to-relay')) {
			await query<ContentIdByReferenceIdQuery$data, ContentIdByReferenceIdQuery$variables>({
				query: ContentIdByReferenceIdQuery_Relay,
				variables: {
					id: contentId,
				},
			});
		} else {
			await query<ContentIdByReferenceIdQueryType, ContentIdByReferenceIdQueryVariables>({
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: ContentIdByReferenceIdQuery,
				variables: {
					id: contentId,
				},
			});
		}
	} else {
		if (fg('confluence_whiteboard-common-apollo-to-relay')) {
			await query<WhiteboardUnifiedQuery$data, WhiteboardUnifiedQuery$variables>({
				query: WhiteboardUnifiedQuery_Relay,
				variables: {
					contentId,
					spaceKey,
					// The DSP query doesn't currently user the content version value, so using a fixed value
					contentVersion: 1,
				},
			});
		} else {
			await query<WhiteboardUnifiedQueryType, WhiteboardUnifiedQueryVariables>({
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: WhiteboardUnifiedQuery,
				variables: {
					contentId,
					spaceKey,
					// The DSP query doesn't currently user the content version value, so using a fixed value
					contentVersion: 1,
				},
			});
		}
	}
}

async function preloadSpace(spaceKey: string): Promise<void> {
	if (fg('confluence_whiteboard-common-apollo-to-relay')) {
		await query<SpaceQuery$data, SpaceQuery$variables>({
			query: SpaceQuery_Relay,
			variables: {
				spaceKey,
			},
		});
	} else {
		await query<SpaceQueryType, SpaceQueryVariables>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: SpaceQuery,
			variables: {
				spaceKey,
			},
		});
	}
}
